import {
  Component,
  OnInit,
  ViewEncapsulation,
  ChangeDetectorRef,
  Input,
  OnChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { filter, switchMap } from 'rxjs/operators';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';

import { Emails, Mail } from 'app/shared/components/widgets/mailbox-mail-list/mailbox.types';
import { Account } from 'app/core/models';
import { MailboxService } from './services/mailbox.service';
import { MailboxMailViewComponent } from './details/details.component';
import { ConfirmService } from '../dialog/confirm/confirm.service';

@Component({
  selector: 'mailbox-mail-list',
  templateUrl: './mailbox-mail-list.component.html',
  styleUrls: ['./mailbox-mail-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MailboxMailListComponent extends OnDestroyMixin implements OnInit, OnChanges {
  @Input() account: Account;
  @Input() relatedToId: number;
  @Input() relatedToType: string;
  @Input() view: string;

  @Input() isSyncing: boolean;
  @Output() isSyncingChange = new EventEmitter<boolean>();

  mails: Mail[] = [];
  mailsLoading: boolean = false;

  private _accountId: number;

  constructor(
    private readonly _mailboxService: MailboxService,
    private readonly _matDialog: MatDialog,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly confirmService: ConfirmService,
  ) {
    super();
  }

  ngOnInit(): void {
    this._accountId = parseInt(this.account.id);
    this.getMails();
  }

  ngOnChanges(): void {
    if (this.isSyncing) {
      this._mailboxService
        .refreshMails(this._accountId)
        .pipe(
          untilComponentDestroyed(this),
          switchMap(() => this._mailboxService.getMails(this._accountId, this.relatedToId, this.relatedToType)),
        )
        .subscribe((response: Emails) => {
          if (response) {
            this.mails = response.emails;
            this.mailsLoading = false;
            this.isSyncingChange.emit(false);
            this._changeDetectorRef.markForCheck();
          }
        });
    }
  }

  getMails(): void {
    this.mailsLoading = true;
    this._changeDetectorRef.markForCheck();

    this._mailboxService
      .getMails(this._accountId, this.relatedToId, this.relatedToType)
      .pipe(untilComponentDestroyed(this))
      .subscribe((response: Emails) => {
        if (response) {
          this.mails = response.emails;
          this.mailsLoading = false;
          this._changeDetectorRef.markForCheck();
        }
      });
  }

  viewEmail(email: Mail): void {
    const matDialog = this._matDialog.open(MailboxMailViewComponent, {
      panelClass: 'mailbox-compose-dialog',
      minWidth: '100vw',
      height: '100vh',
      data: { email, accountId: this._accountId },
    });

    matDialog
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.getMails();
      });
  }

  onDeleteEmail(event: MouseEvent, email: Mail): void {
    event.stopPropagation();

    this.confirmService
      .openConfirmationModal({
        message: 'Are you sure you want to delete this email?',
      })
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        this._mailboxService
          .deleteMail(this._accountId, email.id)
          .pipe(untilComponentDestroyed(this))
          .subscribe(() => this.getMails());
      });
  }

  trackByFn(index: number, item: Mail): number | string {
    return item.id || index;
  }

  isDealView(): boolean {
    return this.view === 'deal';
  }
}
